<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const props = defineProps({
  activity: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const activityConfig = computed(() => {
  const type = props.activity.activityType;

  const configs = {
    deal_created: {
      icon: 'add-circle',
      color: 'text-green-600 bg-green-100 dark:bg-green-900/30',
      label: t('CRM.ACTIVITY.TYPES.DEAL_CREATED'),
    },
    deal_won: {
      icon: 'checkmark-circle',
      color: 'text-green-600 bg-green-100 dark:bg-green-900/30',
      label: t('CRM.ACTIVITY.TYPES.DEAL_WON'),
    },
    deal_lost: {
      icon: 'dismiss-circle',
      color: 'text-red-600 bg-red-100 dark:bg-red-900/30',
      label: t('CRM.ACTIVITY.TYPES.DEAL_LOST'),
    },
    deal_reopened: {
      icon: 'arrow-reply',
      color: 'text-woot-600 bg-woot-100 dark:bg-woot-900/30',
      label: t('CRM.ACTIVITY.TYPES.DEAL_REOPENED'),
    },
    stage_changed: {
      icon: 'arrow-right',
      color: 'text-violet-600 bg-violet-100 dark:bg-violet-900/30',
      label: t('CRM.ACTIVITY.TYPES.STAGE_CHANGED'),
    },
    value_updated: {
      icon: 'currency-dollar-rupee',
      color: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30',
      label: t('CRM.ACTIVITY.TYPES.VALUE_UPDATED'),
    },
    probability_updated: {
      icon: 'data-pie',
      color: 'text-woot-600 bg-woot-100 dark:bg-woot-900/30',
      label: t('CRM.ACTIVITY.TYPES.PROBABILITY_UPDATED'),
    },
    expected_close_updated: {
      icon: 'calendar-ltr',
      color: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30',
      label: t('CRM.ACTIVITY.TYPES.EXPECTED_CLOSE_UPDATED'),
    },
    note_added: {
      icon: 'document',
      color: 'text-slate-600 bg-slate-100 dark:bg-slate-700',
      label: t('CRM.ACTIVITY.TYPES.NOTE_ADDED'),
    },
    checklist_updated: {
      icon: 'checkmark',
      color: 'text-green-600 bg-green-100 dark:bg-green-900/30',
      label: t('CRM.ACTIVITY.TYPES.CHECKLIST_UPDATED'),
    },
    agent_assigned: {
      icon: 'person-add',
      color: 'text-woot-600 bg-woot-100 dark:bg-woot-900/30',
      label: t('CRM.ACTIVITY.TYPES.AGENT_ASSIGNED'),
    },
    agent_removed: {
      icon: 'person-delete',
      color: 'text-red-600 bg-red-100 dark:bg-red-900/30',
      label: t('CRM.ACTIVITY.TYPES.AGENT_REMOVED'),
    },
  };

  return (
    configs[type] || {
      icon: 'info',
      color: 'text-slate-600 bg-slate-100 dark:bg-slate-700',
      label: type,
    }
  );
});

const formattedTime = computed(() => {
  if (!props.activity.createdAt) return '';
  return formatDistanceToNow(new Date(props.activity.createdAt), {
    addSuffix: true,
    locale: ptBR,
  });
});

const userName = computed(() => {
  return props.activity.user?.name || t('CRM.ACTIVITY.SYSTEM');
});

const hasAvatar = computed(() => {
  return props.activity.user?.avatarUrl;
});
</script>

<template>
  <div class="activity-item">
    <!-- Icon/Avatar -->
    <div class="activity-item__icon-wrapper">
      <div class="activity-item__icon" :class="activityConfig.color">
        <fluent-icon :icon="activityConfig.icon" size="14" />
      </div>
      <div class="activity-item__line" />
    </div>

    <!-- Content -->
    <div class="activity-item__content">
      <div class="activity-item__header">
        <span class="activity-item__type">
          {{ activityConfig.label }}
        </span>
        <span class="activity-item__time">
          {{ formattedTime }}
        </span>
      </div>

      <p class="activity-item__description">
        {{ activity.description }}
      </p>

      <!-- User info -->
      <div class="activity-item__user">
        <img
          v-if="hasAvatar"
          :src="activity.user.avatarUrl"
          :alt="userName"
          class="activity-item__avatar"
        />
        <span
          v-else
          class="activity-item__avatar activity-item__avatar--placeholder"
        >
          {{ userName.charAt(0).toUpperCase() }}
        </span>
        <span class="activity-item__user-name">{{ userName }}</span>
      </div>

      <!-- Metadata for specific types -->
      <div
        v-if="activity.metadata && Object.keys(activity.metadata).length > 0"
        class="activity-item__metadata"
      >
        <!-- Note content -->
        <div
          v-if="
            activity.activityType === 'note_added' && activity.metadata.content
          "
          class="activity-item__note"
        >
          {{ activity.metadata.content }}
        </div>

        <!-- Reason for won/lost -->
        <div
          v-if="
            (activity.activityType === 'deal_won' ||
              activity.activityType === 'deal_lost') &&
            activity.metadata.reason
          "
          class="activity-item__reason"
        >
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          <strong>{{ t('CRM.ACTIVITY.REASON') }}:</strong>
          {{ activity.metadata.reason }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.activity-item {
  @apply flex gap-3;
}

.activity-item__icon-wrapper {
  @apply flex flex-col items-center;
}

.activity-item__icon {
  @apply w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0;
}

.activity-item__line {
  @apply w-0.5 flex-1 bg-slate-200 dark:bg-slate-700 mt-1;
}

.activity-item:last-child .activity-item__line {
  @apply hidden;
}

.activity-item__content {
  @apply flex-1 pb-4;
}

.activity-item__header {
  @apply flex items-center justify-between mb-1;
}

.activity-item__type {
  @apply text-sm font-medium text-slate-800 dark:text-slate-200;
}

.activity-item__time {
  @apply text-xs text-slate-500 dark:text-slate-400;
}

.activity-item__description {
  @apply text-sm text-slate-600 dark:text-slate-400 mb-2;
}

.activity-item__user {
  @apply flex items-center gap-1.5;
}

.activity-item__avatar {
  @apply w-5 h-5 rounded-full object-cover;
}

.activity-item__avatar--placeholder {
  @apply flex items-center justify-center bg-slate-300 dark:bg-slate-600 text-xs font-medium text-slate-600 dark:text-slate-300;
}

.activity-item__user-name {
  @apply text-xs text-slate-500 dark:text-slate-400;
}

.activity-item__metadata {
  @apply mt-2;
}

.activity-item__note {
  @apply p-2 bg-slate-100 dark:bg-slate-700 rounded text-sm text-slate-700 dark:text-slate-300 italic;
}

.activity-item__reason {
  @apply text-sm text-slate-600 dark:text-slate-400;
}
</style>
